import { useState, useRef, useEffect } from 'react';
import { message, Space } from 'antd';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { calculatePackagePrice } from 'services/api/pay';
import { isUndefined } from 'lodash-es';
import { PackageSelectPanel } from './PackageSelectPanel';
import { DurationSelectPanel } from './DurationSelectPanel';
import { PanelTitle } from './Common';

const noAddTime: Pay.DurationDescV2 = {
  id: 0,
  name: '0',
  price: 0,
  durationNum: 0,
  currencyAmount: [],
};

export interface PackageDetailProps {
  orderType: Pay.OrderType;
  orgId: number;
  currency: General.Currency;
  orderedPackages: Pay.OrderedPackage[];
  packageInfo: Pay.PackageInfoV2;
  checkedPackageId?: Pay.PackageId;
  curWorkingPackage?: Pay.OrderedPackage;
  setAccountNum: React.Dispatch<React.SetStateAction<number>>;
  setCanOrder: React.Dispatch<React.SetStateAction<boolean>>;
  isEnterprise: boolean;
  setIsEnterprise: React.Dispatch<React.SetStateAction<boolean>>;
  onPackagePriceChange?: (value?: Pay.CalculatePackagePriceRst) => void;
  onPackageChange?: (value: Pay.PackageId | undefined) => void;
  onDurationChange?: (value: Pay.DurationNum) => void;
}

// 升级/续订套餐
function PackageDlgDetail(props: PackageDetailProps) {
  const {
    packageInfo,
    setAccountNum,
    checkedPackageId,
    orderedPackages,
    orgId,
    currency,
    orderType,
    setCanOrder,
    isEnterprise,
    setIsEnterprise,
    curWorkingPackage,
    onPackagePriceChange,
    onPackageChange,
    onDurationChange,
  } = props;

  const { t } = useTranslate();

  const [durationDesc, setDurationDesc] = useState<Pay.PackageV2['durationDesc']>([]);

  const { packageData, recommendData } = packageInfo;
  const { recommendPackageId, recommendDurationId } = recommendData;
  // 升级套餐默认选择不增加时长
  const [chkDuration, setChkDuration] = useState(orderType === 'UPGRADE' ? 0 : recommendDurationId);
  const chkExtraDurationNum = useRef(0);

  const curRenewPackage = orderedPackages.find((v) => v.status === 0);

  useEffect(() => {
    if (!curWorkingPackage) {
      return;
    }
    packageData.forEach((account) => {
      const { id } = account;
      let duDesc = account.durationDesc;
      const checked = id === checkedPackageId;

      if (checked) {
        if (orderType === 'UPGRADE') {
          duDesc = [noAddTime, ...duDesc];
        }
        setDurationDesc(duDesc);
        duDesc.some((duration) => {
          const { id, durationNum, extraDurationNum } = duration;
          const checked = id === chkDuration;

          if (checked && checkedPackageId) {
            chkExtraDurationNum.current = extraDurationNum ?? 0;
            calculatePackagePrice({
              orgId,
              orderType,
              durationNum,
              newPackageId: checkedPackageId,
              currency,
            })
              .then((d) => {
                onDurationChange?.(durationNum);
                setCanOrder(true);
                onPackagePriceChange?.(d);
              })
              .catch((e) => {
                message.error(`/calculatePackagePrice ${e?.message}`);
                setCanOrder(false);
                onPackagePriceChange?.(undefined);
              });
          }
          return checked;
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    packageData,
    chkDuration,
    checkedPackageId,
    orgId,
    curWorkingPackage,
    orderType,
    currency,
    setCanOrder,
  ]);

  return curWorkingPackage ? (
    <Space size={16} direction="vertical" className="w-full">
      <PanelTitle>
        {t('market.当前套餐')}
        <PackageDesc>{t(`套餐.${curWorkingPackage.id}`)}</PackageDesc>
      </PanelTitle>
      <PackageSelectPanel
        value={checkedPackageId}
        isEnterprise={isEnterprise}
        recommendPackageId={orderType === 'UPGRADE' ? undefined : recommendPackageId}
        list={packageData}
        disabled={(_, item) => {
          if (orderType === 'UPGRADE' && item) {
            const itemIndex = packageData.findIndex((v) => v.id === item.id);
            const workingPackageIndex = packageData.findIndex((v) => v.id === curWorkingPackage.id);
            return itemIndex <= workingPackageIndex;
          }
          return false;
        }}
        onChange={(isEnterprise, selected) => {
          setIsEnterprise(isEnterprise);
          if (isEnterprise) {
            // 企业版
            onPackageChange?.(undefined);
            setDurationDesc([]);
          } else if (selected) {
            const { accountNum, id, durationDesc } = selected;
            setAccountNum(accountNum);
            onPackageChange?.(id);
            if (orderType === 'UPGRADE') {
              setDurationDesc([noAddTime, ...durationDesc]);
              setChkDuration(0);
            } else {
              setDurationDesc(durationDesc);
              setChkDuration(recommendDurationId);
            }
          }
        }}
      />
      {durationDesc.length ? (
        <DurationSelectPanel
          durationId={chkDuration}
          packageId={checkedPackageId}
          orderType={orderType}
          currency={currency}
          packageData={packageData}
          recommendDurationId={orderType === 'UPGRADE' ? undefined : recommendDurationId}
          canIncreaseDuration={!curRenewPackage}
          list={durationDesc}
          disabled={(value) => !isUndefined(curRenewPackage) && value.id > 0}
          onChange={(value) => {
            setChkDuration(value.id);
          }}
        />
      ) : null}
    </Space>
  ) : null;
}

export default PackageDlgDetail;

export const Detail = styled.section`
  margin-bottom: 24px;
  h4 {
    display: flex;
    align-items: center;
    height: 20px;
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: normal;
    color: #232426;
    span.packtip {
      margin-left: 8px;
      font-size: 12px;
      color: #f2a100;
      background: #faedd2;
      padding: 0 4px;
      border-radius: 2px;
    }
  }
`;
const PackageDesc = styled.span`
  color: #515359;
  font-weight: 400;
  margin-left: 8px;
`;
